import React, { useEffect, useRef, useState } from "react";
import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Divider,
  Button,
  TextField,
  Chip,
} from "@mui/material";
import { API_URL } from "../../../Config/api";

const StepFourHHCServiceRef = ({
  formData,

  errors,
  setFormData,
  handleChange,
}) => {
  // const port = process.env.REACT_APP_API_KEY;
  const accessToken = localStorage.getItem("token");

  const [services, setServices] = useState([]);
  const [subServicesMap, setSubServicesMap] = useState({});
  const [selectedServices, setSelectedServices] = useState({});

  /* ================= SIGNATURE STATES ================= */

  const patientCanvasRef = useRef(null);
  const physicianCanvasRef = useRef(null);

  const [patientSignaturePreview, setPatientSignaturePreview] = useState(null);
  const [physicianSignaturePreview, setPhysicianSignaturePreview] =
    useState(null);

  const [isDrawingPatient, setIsDrawingPatient] = useState(false);
  const [isDrawingPhysician, setIsDrawingPhysician] = useState(false);
  const [openService, setOpenService] = useState(null);

  /* ================= CANVAS DRAW ================= */

  const startDrawing = (e, canvasRef, setDrawing) => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    setDrawing(true);
  };

  const draw = (e, canvasRef, isDrawing) => {
    if (!isDrawing) return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    ctx.stroke();
  };

  const stopDrawing = (setDrawing) => {
    setDrawing(false);
  };
  /* ================= SAVE SIGNATURE ================= */

  const savePatientSignature = () => {
    const dataURL = patientCanvasRef.current.toDataURL("image/png");
    setPatientSignaturePreview(dataURL);

    setFormData((prev) => ({
      ...prev,
      patient_signature: dataURL,
    }));
  };

  const savePhysicianSignature = () => {
    const dataURL = physicianCanvasRef.current.toDataURL("image/png");
    setPhysicianSignaturePreview(dataURL);

    setFormData((prev) => ({
      ...prev,
      physician_signature: dataURL,
    }));
  };

  /* ================= CLEAR SIGNATURE ================= */

  const clearPatientSignature = () => {
    const canvas = patientCanvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);

    setPatientSignaturePreview(null);

    setFormData((prev) => ({
      ...prev,
      patient_signature: null,
    }));
  };

  const clearPhysicianSignature = () => {
    const canvas = physicianCanvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);

    setPhysicianSignaturePreview(null);

    setFormData((prev) => ({
      ...prev,
      physician_signature: null,
    }));
  };
  /* ================= FILE UPLOAD ================= */

  const handlePatientUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const preview = URL.createObjectURL(file);
    setPatientSignaturePreview(preview);

    setFormData((prev) => ({
      ...prev,
      patient_signature: file,
    }));
  };

  const handlePhysicianUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const preview = URL.createObjectURL(file);
    setPhysicianSignaturePreview(preview);

    setFormData((prev) => ({
      ...prev,
      physician_signature: file,
    }));
  };

  useEffect(() => {
    if (
      formData.patient_signature &&
      typeof formData.patient_signature === "string"
    ) {
      setPatientSignaturePreview(formData.patient_signature);
    }

    if (
      formData.physician_signature &&
      typeof formData.physician_signature === "string"
    ) {
      setPhysicianSignaturePreview(formData.physician_signature);
    }
  }, [formData.patient_signature, formData.physician_signature]);

  /* ================= FETCH SERVICES ================= */

  useEffect(() => {
    fetch(`${API_URL}/web/agg_hhc_services_api_web_form`, {
      headers: {
        // Authorization: `Bearer ${accessToken}`,
      },
    })
      .then((res) => res.json())
      .then(setServices)
      .catch(console.error);
  }, [API_URL, accessToken]);

  const fetchSubServices = async (srv_id) => {
    if (subServicesMap[srv_id]) return;

    const res = await fetch(`${API_URL}/web/agg_hhc_sub_services_api/${srv_id}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const data = await res.json();
    setSubServicesMap((prev) => ({ ...prev, [srv_id]: data }));
  };

  /* ================= PREFILL (EDIT MODE) ================= */

  useEffect(() => {
    if (!formData.services?.length) return;

    const prefilled = {};
    formData.services.forEach((srv_id) => {
      prefilled[srv_id] = { sub_srv_ids: [] };
      fetchSubServices(srv_id);
    });
    setSelectedServices(prefilled);
  }, [formData.services]);

  useEffect(() => {
    if (!formData.sub_services?.length) return;

    setSelectedServices((prev) => {
      const updated = { ...prev };

      Object.entries(subServicesMap).forEach(([srv_id, subs]) => {
        const matched = subs
          .filter((s) => formData.sub_services.includes(s.sub_srv_id))
          .map((s) => s.sub_srv_id);

        if (matched.length) {
          updated[srv_id] = { sub_srv_ids: matched };
        }
      });

      return updated;
    });
  }, [subServicesMap, formData.sub_services]);

  /* ================= HANDLERS ================= */

  const handleServiceToggle = (checked, srv_id) => {
    if (checked) {
      setSelectedServices((prev) => ({
        ...prev,
        [srv_id]: { sub_srv_ids: [] },
      }));

      setFormData((prev) => ({
        ...prev,
        services: [...prev.services, srv_id],
      }));

      fetchSubServices(srv_id);
    } else {
      setSelectedServices((prev) => {
        const copy = { ...prev };
        delete copy[srv_id];
        return copy;
      });

      setFormData((prev) => ({
        ...prev,
        services: prev.services.filter((id) => id !== srv_id),
        sub_services: prev.sub_services.filter(
          (id) => !subServicesMap[srv_id]?.some((s) => s.sub_srv_id === id)
        ),
      }));
    }
  };

  const handleSubServiceChange = (srv_id, sub_srv_ids) => {
    setSelectedServices((prev) => ({
      ...prev,
      [srv_id]: { sub_srv_ids },
    }));

    setFormData((prev) => ({
      ...prev,
      sub_services: Array.from(
        new Set([
          ...prev.sub_services.filter(
            (id) => !subServicesMap[srv_id]?.some((s) => s.sub_srv_id === id)
          ),
          ...sub_srv_ids,
        ])
      ),
    }));
  };

  /* ================= UI ================= */

  return (
    <>
<Card
  sx={{
    p: { xs: 1.5, sm: 2 },
    borderRadius: 3,
    backgroundColor: "#fafafa",
     border: errors.services ? "1px solid #d32f2f" : "1px solid transparent",
  }}
>
  <Typography
    fontWeight={600}
    color="primary"
    sx={{ fontSize: { xs: "0.95rem", sm: "1rem" } }}
  >
    SELECT SERVICES
  </Typography>

{/* ✅ ERROR MESSAGE */}
{errors.services && (
  <Typography
    color="error"
    variant="caption"
    sx={{ mt: 0.5, display: "block" }}
  >
    {errors.services}
  </Typography>
)}

  <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />

  {services.map((srv) => (
    <Box
      key={srv.srv_id}
      sx={{
        mb: { xs: 1.5, sm: 2 },
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      {/* SERVICE CHECKBOX */}
      <FormControlLabel
          sx={{
    m: 0,
    display: "flex",
    alignItems: "center",     
    gap: 1,                   // spacing between checkbox & text
    ".MuiFormControlLabel-label": {
      fontSize: { xs: "0.9rem", sm: "0.95rem" },
      lineHeight: 1.3,
      whiteSpace: "normal",   // long text wrap hoga
    },
  }}
        control={
          <Checkbox
            size="small"
            checked={!!selectedServices[srv.srv_id]}
            onChange={(e) => {
              handleServiceToggle(e.target.checked, srv.srv_id);
              setOpenService(e.target.checked ? srv.srv_id : null);
            }}
          />
        }
        label={srv.service_title}
      />

      {/* SUB SERVICES */}
      {selectedServices[srv.srv_id] &&
        subServicesMap[srv.srv_id]?.length > 0 && (
          <Select
            multiple
            fullWidth
            size="small"
            open={openService === srv.srv_id}
            onOpen={() => setOpenService(srv.srv_id)}
            onClose={() => setOpenService(null)}
            value={selectedServices[srv.srv_id]?.sub_srv_ids || []}
            displayEmpty
            sx={{ mt: 0.5, ml: 1 }}
            renderValue={(selected) =>
              selected.length ? (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    maxHeight: 64,
                    overflowY: "auto",
                  }}
                  onClick={() => setOpenService(srv.srv_id)}
                >
                  {subServicesMap[srv.srv_id]
                    .filter((s) => selected.includes(s.sub_srv_id))
                    .map((s) => (
                      <Chip
                        key={s.sub_srv_id}
                        label={s.recommomded_service}
                        size="small"
                        sx={{ maxWidth: "100%" }}
                      />
                    ))}
                </Box>
              ) : (
                <Typography
                  color="text.secondary"
                  sx={{ fontSize: "0.85rem" }}
                  onClick={() => setOpenService(srv.srv_id)}
                >
                  Select Sub-Services
                </Typography>
              )
            }
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
              PaperProps: {
                sx: {
                  maxHeight: "60vh",
                  borderRadius: 2,
                },
              },
            }}
            onChange={(e) =>
              handleSubServiceChange(srv.srv_id, e.target.value)
            }
          >
            {subServicesMap[srv.srv_id].map((sub) => (
              <MenuItem
                key={sub.sub_srv_id}
                value={sub.sub_srv_id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 0.5,
                }}
              >
                <Checkbox
                  size="small"
                  checked={
                    selectedServices[srv.srv_id]?.sub_srv_ids.includes(
                      sub.sub_srv_id
                    ) || false
                  }
                />
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    lineHeight: 1.3,
                    whiteSpace: "normal",
                  }}
                >
                  {sub.recommomded_service}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        )}
    </Box>
  ))}
</Card>

      <Card
        variant="outlined"
        sx={{
          mt: 3,
          borderRadius: 3,
          boxShadow: 3,
          backgroundColor: "#fafafa",
        }}
      >
        <CardContent>
          <Typography
            color="primary"
            sx={{ fontWeight: 600, mb: 1, fontSize: "1rem" }}
          >
            Patient / Guardian Details
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            {/* Patient Name */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Patient Name"
                fullWidth
                size="small"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                 InputProps={{
      readOnly: true,
    }}
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>

            {/* Form Fillup Date */}
<Grid item xs={12} md={4}>
  <TextField
    fullWidth
    size="small"
    type="datetime-local"
    label="Form Fill-up Date & Time"
    InputLabelProps={{ shrink: true }}

    inputProps={{
      max: new Date().toISOString().slice(0, 16), // 🚫 future datetime blocked
    }}

    value={
      formData.form_fillup_date
        ? formData.form_fillup_date.slice(0, 16) // YYYY-MM-DDTHH:mm
        : ""
    }

    onChange={(e) => {
      const selectedDateTime = e.target.value; // YYYY-MM-DDTHH:mm

      // 🔑 convert to backend format
      const dateTimeWithSeconds = selectedDateTime
        ? `${selectedDateTime}:00`
        : "";

      handleChange("form_fillup_date", dateTimeWithSeconds);
    }}

    error={!!errors.form_fillup_date}
    helperText={errors.form_fillup_date}
  />
</Grid>




            {/* Notify Name */}
            
              <Grid item xs={12} md={4}>
  <TextField
    label="Physician Name"
    fullWidth
    size="small"
    value={formData.notify_name || ""}
    InputProps={{
      readOnly: true,
    }}
  />
</Grid>
           

            {/* ================= PATIENT SIGNATURE ================= */}
            <Grid item xs={12} md={6}>
              <Typography sx={{ mb: 1, fontWeight: 600 }}>
                Patient / Guardian Signature
              </Typography>
                        {errors.patient_signature && (
  <Typography color="error" variant="caption">
    {errors.patient_signature}
  </Typography>
)}

              {patientSignaturePreview && (
                <Box sx={{ mb: 2, textAlign: "center" }}>
                  <img
                    src={patientSignaturePreview}
                    alt="Patient Signature"
                    style={{ maxHeight: 120 }}
                  />
                </Box>
              )}

              <Box
                sx={{
                  border: "1px dashed #9e9e9e",
                  borderRadius: 2,
                  height: 160,
                  mb: 2,
                  backgroundColor: "#fff",
                }}
              >
                <canvas
                  ref={patientCanvasRef}
                  width={500}
                  height={160}
                  style={{ width: "100%", height: "100%" }}
                  onMouseDown={(e) =>
                    startDrawing(e, patientCanvasRef, setIsDrawingPatient)
                  }
                  onMouseMove={(e) =>
                    draw(e, patientCanvasRef, isDrawingPatient)
                  }
                  onMouseUp={() => stopDrawing(setIsDrawingPatient)}
                  onMouseLeave={() => stopDrawing(setIsDrawingPatient)}
                />
              </Box>
    

              <Grid container spacing={1}>
                <Grid item>
                  <Button
                    size="small"
                    color="error"
                    onClick={clearPatientSignature}
                  >
                    Clear
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={savePatientSignature}
                  >
                    Save
                  </Button>
                </Grid>
                <Grid item>
                  <Button size="small" component="label">
                    Upload
                    <input
                      hidden
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handlePatientUpload}
                    />
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            {/* ================= PHYSICIAN SIGNATURE ================= */}
            <Grid item xs={12} md={6}>
              <Typography sx={{ mb: 1, fontWeight: 600 }}>
                Physician Signature
              </Typography>
              {errors.physician_signature && (
  <Typography color="error" variant="caption">
    {errors.physician_signature}
  </Typography>
)}

              {formData.physician_signature && (
                <Box sx={{ mb: 2, textAlign: "center" }}>
                  <img
                    src={
                      typeof formData.physician_signature === "string"
                        ? formData.physician_signature
                        : URL.createObjectURL(formData.physician_signature)
                    }
                    alt="Physician Signature"
                    style={{ maxHeight: 120 }}
                  />
                </Box>
              )}

              <Box
                sx={{
                  border: "1px dashed #9e9e9e",
                  borderRadius: 2,
                  height: 160,
                  mb: 2,
                  backgroundColor: "#fff",
                }}
              >
                <canvas
                  ref={physicianCanvasRef}
                  width={500}
                  height={160}
                  style={{ width: "100%", height: "100%" }}
                  onMouseDown={(e) =>
                    startDrawing(e, physicianCanvasRef, setIsDrawingPhysician)
                  }
                  onMouseMove={(e) =>
                    draw(e, physicianCanvasRef, isDrawingPhysician)
                  }
                  onMouseUp={() => stopDrawing(setIsDrawingPhysician)}
                  onMouseLeave={() => stopDrawing(setIsDrawingPhysician)}
                />
              </Box>

              <Grid container spacing={1}>
                <Grid item>
                  <Button
                    size="small"
                    color="error"
                    onClick={clearPhysicianSignature}
                  >
                    Clear
                  </Button>
                </Grid>

                <Grid item>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={savePhysicianSignature}
                  >
                    Save
                  </Button>
                </Grid>

                <Grid item>
                  <Button size="small" component="label">
                    Upload
                    <input
                      hidden
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handlePhysicianUpload}
                    />
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
};

export default StepFourHHCServiceRef;
